// LiveTimeline.js — Horizontal timeline showing run lifecycle events.
// Actual events are filled markers, projected events are hollow markers.

import React from 'react';

const EVENT_LABELS = {
  auction: 'Auction',
  auction_settlement: 'Settlement',
  settlement: 'Settlement',
  maturity: 'Maturity',
  rollover: 'Rollover',
  exit: 'Exit',
  completion: 'Completed',
};

function TimelineMarker({ event, isActual, isActive }) {
  const baseClasses = 'w-4 h-4 rounded-full border-2 flex-shrink-0';
  const colorClasses = isActive
    ? 'border-blue-600 bg-blue-600'
    : isActual
    ? 'border-emerald-500 bg-emerald-500'
    : 'border-gray-300 bg-white';

  return (
    <div className="flex flex-col items-center gap-1 min-w-[72px]">
      <div className={`${baseClasses} ${colorClasses}`} />
      <span className="text-[10px] font-medium text-gray-600 text-center leading-tight">
        {EVENT_LABELS[event.event_type] || event.event_type}
      </span>
      <span className="text-[10px] text-gray-400">
        {event.event_date || event.date || ''}
      </span>
      {!isActual && (
        <span className="text-[9px] text-amber-600 font-medium">Projected</span>
      )}
    </div>
  );
}

export default function LiveTimeline({ cashflows = [], currentStatus, nextEventType }) {
  // Build timeline from cashflows + projected future events
  const actualEvents = cashflows
    .filter(cf => cf.is_actual !== false && cf.is_actual !== 0)
    .map(cf => ({ ...cf, actual: true }));

  // Add projected events based on current status
  const projectedEvents = [];
  const statusOrder = ['auction', 'settlement', 'maturity', 'exit'];
  const currentIdx = statusOrder.indexOf(nextEventType || 'exit');

  if (currentIdx >= 0) {
    for (let i = currentIdx; i < statusOrder.length; i++) {
      const type = statusOrder[i];
      // Don't add if we already have an actual event for this
      if (!actualEvents.find(e => e.event_type === type || e.event_type === `auction_${type}`)) {
        projectedEvents.push({
          event_type: type,
          actual: false,
          event_date: '',
        });
      }
    }
  }

  const allEvents = [...actualEvents, ...projectedEvents];

  if (allEvents.length === 0) {
    return (
      <div className="text-xs text-gray-400 text-center py-3">
        No events yet
      </div>
    );
  }

  return (
    <div className="py-3">
      <div className="flex items-start overflow-x-auto gap-2">
        {allEvents.map((event, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <div className="flex-shrink-0 mt-2 w-6 h-px bg-gray-200" />
            )}
            <TimelineMarker
              event={event}
              isActual={event.actual}
              isActive={
                event.event_type === nextEventType ||
                event.event_type === `auction_${nextEventType}`
              }
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
